import React, { useState, useEffect } from "react";
import { random } from "lodash"; // Import the Lodash library for generating random numbers
import { motion } from "framer-motion"; // Import the Framer Motion library for animation
import { Link as LinkRouter } from "react-router-dom";
import { makeProjectURL } from "./makeURL";
import allProjectDicts from "../content/allContent";
import { Button, Slider } from "@material-ui/core";
// import { classNames } from "classnames"; // Import the Classnames library for handling CSS classes
import { isImage } from "./utils";


// Monkey-path the seed function
Math.seed = function(s) {
  var mask = 0xffffffff;
  var m_w  = (123456789 + s) & mask;
  var m_z  = (987654321 - s) & mask;

  return function() {
    m_z = (36969 * (m_z & 65535) + (m_z >>> 16)) & mask;
    m_w = (18000 * (m_w & 65535) + (m_w >>> 16)) & mask;

    var result = ((m_z << 16) + (m_w & 65535)) >>> 0;
    result /= 4294967296;
    return result;
  }
}


function seededRandomColor(seed) {
  // Create a seeded random number generator
  var seededRandom = Math.seed(seed);
  
  // Generate random values for red, green, and blue (RGB) channels
  const r = Math.floor(seededRandom() * 256);
  const g = Math.floor(seededRandom() * 256);
  const b = Math.floor(seededRandom() * 256);
  
  // Combine RGB values into a single CSS color code
  const colorCode = `rgb(${r}, ${g}, ${b})`;
  
  return colorCode;
}


function seededInterpRandomColor(seed, color1, color2) {
  // Create a seeded random number generator
  var seededRandom = Math.seed(seed);
  
  const weight = seededRandom()
  const r = Math.floor(color1[0] * weight + color2[0] * (1 - weight));
  const g = Math.floor(color1[1] * weight + color2[1] * (1 - weight));
  const b = Math.floor(color1[2] * weight + color2[2] * (1 - weight));
  
  return `rgb(${r}, ${g}, ${b})`;
}


function shuffle(array) {
  let currentIndex = array.length,  randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {

    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }

  return array;
}


const MotionGrid = ( { numCols=3, numRows=3, cardWidth=200, projectDictsList } ) => {

  numCols = numCols+2;    // left and rightmost cols are hidden and used for adding new elements
  const color1 = [255, 119, 89];    // orange
  const color2 = [119, 236, 197];   // turquoise

  const idxCount = numCols*numRows > projectDictsList.length ? numCols*numRows+1 : projectDictsList.length;

  let projectIdxs = Array.from(Array(idxCount).keys());
  projectIdxs = shuffle(projectIdxs);

  var initGrid = [];
  for (let i = 0; i < numRows; i++) {
    let row = [];
    for (let j = 0; j < numCols; j++) {
      row.push(projectIdxs.pop());
    };
    initGrid.push(row);
  }

  // Create a 2D array to hold the positions of your images in the grid
  const [grid, setGrid] = useState(initGrid); 
  // Array that contains all idxs of projects not currently in the grid.
  const [idxsStash, setIdxsStash] = useState(projectIdxs); 
  const [rowsLeftShift, setRowsLeftShift] = useState(new Array(numRows).fill(true));

  useEffect(() => {
    const interval = setInterval(() => {
      // const randomRowIndex = random(1, numRows-2); // Generate a random index between 0 and 4
      const randomRowIndex = random(0, numRows-1); // Generate a random index between 0 and 4
      const leftShift = Math.random() < 1.5; // Generate a random direction (0 for left, 1 for right)
      const newRowsLeftShift = rowsLeftShift;
      rowsLeftShift[randomRowIndex] = leftShift;
      setRowsLeftShift(newRowsLeftShift);

      const row = [...grid[randomRowIndex]];
      const newIdx = idxsStash.pop();   // Get a project index to insert
      var removedIdx = null;
      if ( leftShift ) {
        removedIdx = row.shift();   // remove the first element
        row.push(newIdx);           // add an element to the end
      } 
      else {  
        removedIdx = row.pop();     // remove the last element
        row.unshift(newIdx);        // add an element to the start
      }
      setIdxsStash([...idxsStash, removedIdx]);   // add the removed idx to the stash

      const newGrid = [...grid];
      newGrid[randomRowIndex] = row;
      setGrid(newGrid);
    }, 2000);
    return () => clearInterval(interval);
  }, [grid, rowsLeftShift, idxsStash]);

  function getProjectTitle(idx) {
    if (idx < projectDictsList.length){
      return allProjectDicts[idx].title;
    }
    return "";
  }

  function getProjectTags(idx) {
    if (idx < projectDictsList.length){
      return allProjectDicts[idx].tags;
    }
    return [];
  }
  
  function getProjectImg(idx) {
    if (idx < projectDictsList.length){
      return allProjectDicts[idx].coverImg;
    }
    return null;
  }

  function getProjectBgColor(idx) {
    if (idx < projectDictsList.length){
      return allProjectDicts[idx].mainColor;
    }
    return seededInterpRandomColor(idx, color1, color2);
  }

  return (
    <div className="relative overflow-hidden" style={{height: cardWidth*numRows, width: cardWidth*(numCols-2)}}>
      <div className="absolute" style={{left: -cardWidth}}>
        {grid.map((row, rowIndex) =>
          row.map((cell, columnIndex) => (
            <motion.div
              key={cell}
              className={`absolute w-full p-1`}
              style={{
                height: cardWidth, 
                width: cardWidth,
              }}
              initial={{ opacity: 0, x: rowsLeftShift[rowIndex] ? numCols*cardWidth : 0, y: rowIndex*cardWidth }}
              animate={{ opacity: 1, x: columnIndex*cardWidth, y: rowIndex*cardWidth }}
              transition={{ duration: 0.5 }}
            >
                <div 
                  className="relative w-full h-full group items-center justify-center rounded-3xl overflow-hidden hover:scale-105" 
                  style={{backgroundColor: getProjectBgColor(cell)}}
                >
                  { 
                    getProjectImg(cell) != null ? 
                    <div className="w-full h-full">
                      { isImage(getProjectImg(cell)) ?
                        <img src={getProjectImg(cell)} alt={`image-${cell}`} className="absolute h-full w-full object-cover object-center" /> 
                        :
                        <video 
                          src={getProjectImg(cell)} 
                          alt={`image-${cell}`} 
                          className="absolute h-full w-full object-cover object-center" 
                          autoPlay 
                          loop 
                          muted 
                          controls={false}
                          playsInline
                        />
                      }
                      <div className="flex flex-col w-full h-full items-center invisible justify-between group-hover:visible group-hover:backdrop-blur-sm
                      from-20% to-90% dark:from-20% dark:to-90%
                      from-light-accent2 to-transparent 
                      dark:from-dark-accent dark:to-transparent 
                      bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]
                      "
                      >
                        <div 
                          // className="flex flex-col from-light-accent2 via-light-accent2 via-50% dark:from-dark-accent dark:via-dark-accent dark:via-50% bg-gradient-to-b text-dark dark:text-light text-lg w-full bg-opacity-70 dark:bg-opacity-60"
                          // className="flex flex-col pb-5 from-light-accent2 from-80% dark:from-80% to-transparent dark:to-transparent dark:from-dark-accent bg-gradient-to-b text-dark dark:text-light text-lg w-full bg-opacity-70 dark:bg-opacity-60"
                          className="flex flex-col from-50% to-80% text-dark dark:text-light text-lg w-full"
                            
                          // className="flex flex-col pb-10 from-50% to-80%
                          //   from-light-accent2 to-transparent 
                          //   dark:from-dark-accent dark:from-70% dark:to-transparent 
                          //   bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] 
                          //   text-dark dark:text-light text-lg w-full"
                        >
                          <span className="p-2 md:p-4 font-bold text-center ">{getProjectTitle(cell)}</span>
                          {getProjectTags(cell).map((tag, idx) => <span key={idx} className="text-center text-sm font-mono">{tag}</span>)}
                        </div>
                        <LinkRouter to={makeProjectURL(getProjectTitle(cell))} className="cursor-pointer mb-3 md:mb-10">
                          <div className="p-1 sm:p-1.5 px-4 border-2 border-dark rounded-md bg-light-accent2 dark:bg-dark-accent dark:text-light shadow-3xl hover:bg-opacity-80">More Info</div>
                        </LinkRouter>
                      </div>
                    </div>
                    : null
                  }
                </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

}  


export default MotionGrid;